import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { fetchOne, STATIC } from '@/lib/cms';
import SEO from '@/components/site/SEO';

export default function CaseStudy() {
  const { slug } = useParams();
  const [p, setP] = useState(STATIC.projects.find(x => x.slug === slug) || STATIC.projects[0]);

  useEffect(() => {
    fetchOne('projects', slug, STATIC.projects).then(d => d && setP(d));
  }, [slug]);

  if (!p) return null;
  const others = STATIC.projects.filter(x => x.slug !== p.slug).slice(0, 3);

  return (
    <>
      <SEO title={p.meta_title || `${p.title} — Case study — Slinkflow`} description={p.meta_description || p.tagline} />
      <section className="relative pt-40 lg:pt-56">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Link to="/portfolio" className="text-sm text-ink-500 link-underline">← All work</Link>
          <p className="mt-6 text-xs uppercase tracking-[0.3em] text-cyan">{p.category}</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] md:text-[88px] lg:text-[100px]">
            <TextReveal text={p.title} />
          </h1>
          <p className="mt-4 max-w-3xl text-[22px] leading-snug text-ink-500">{p.tagline}</p>
        </div>
        <FadeUp delay={0.3} className="mt-16">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="relative overflow-hidden rounded-[32px] bg-ink-50">
              <img src={p.cover} alt={p.title} className="aspect-[16/9] w-full object-cover" />
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Metrics */}
      <section className="py-24">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-6 md:grid-cols-3 md:px-10">
          {(p.metrics || []).map((m, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <p className="font-display text-6xl font-light text-ink-900">{m.value}</p>
              <p className="mt-3 text-[15px] text-ink-500">{m.label}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Body */}
      <section className="pb-24">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-ink-400">Client</p>
              <p className="mt-2 font-display text-2xl text-ink-900">{p.title}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-ink-400">Services</p>
              <p className="mt-2 text-[15px] text-ink-700">{p.services_used || 'Brand, Web Design, Web Development, SEO'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-ink-400">Year</p>
              <p className="mt-2 text-[15px] text-ink-700">{p.year || '2026'}</p>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-12">
            <Block t="The challenge" body={p.problem || `${p.title} came to us with a strong product but a website that didn't reflect the quality of what they'd built. Visitors weren't converting and the brand felt generic in a category about to consolidate.`} />
            <Block t="Research & strategy" body={p.research || `Weeks of customer interviews, competitive teardown and analytics archaeology. We landed on a positioning that was sharper, more confident, and unmistakably ${p.title}.`} />
            <Block t="Design & engineering" body={p.solution || 'A bold new visual system, premium typography pairing, and a rhythm of large imagery + ample white space. Every section was wireframed against a single outcome metric.'} features={p.deliverables?.length ? p.deliverables : ["Custom design system","Premium typography","Production-grade React","Accessibility AA+","99/100 Lighthouse"]} />
            <Block t="Outcomes" body={p.outcomes || 'After 90 days the numbers spoke for themselves — and the team finally has a brand that compounds.'} metrics={p.metrics} />
          </div>
        </div>
      </section>

      <section className="bg-ink-50 py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <h2 className="font-display text-[40px] font-light">More <span className="gradient-text font-medium">case studies</span></h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {others.map(o => (
              <Link key={o.id} to={`/portfolio/${o.slug}`} className="group block overflow-hidden rounded-3xl bg-white">
                <div className="aspect-[4/3] overflow-hidden"><img src={o.cover} alt={o.title} className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" loading="lazy" /></div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-widest text-ink-400">{o.category}</p>
                  <h3 className="mt-2 font-display text-2xl text-ink-900">{o.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-900 py-24 text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-8 px-6 md:flex-row md:items-center md:justify-between md:px-10">
          <h3 className="font-display text-[36px] font-light leading-tight md:text-[52px]">Have a project like this?</h3>
          <Link to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-5 text-ink-900">Start a project <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></Link>
        </div>
      </section>
    </>
  );
}

function Block({ t, body, features, metrics }) {
  return (
    <FadeUp>
      <h3 className="font-display text-3xl text-ink-900 md:text-4xl">{t}</h3>
      <p className="mt-4 text-[17px] leading-relaxed text-ink-500">{body}</p>
      {features && (
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {features.map(f => <li key={f} className="flex items-start gap-2 text-[15px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-cyan" /> {f}</li>)}
        </ul>
      )}
      {metrics && (
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {metrics.map((m, i) => (
            <div key={i} className="rounded-2xl bg-cyan-soft p-5">
              <p className="font-display text-3xl text-cyan-700">{m.value}</p>
              <p className="mt-1 text-sm text-ink-700">{m.label}</p>
            </div>
          ))}
        </div>
      )}
    </FadeUp>
  );
}
